/**
 * Raising an order with a supplier.
 *
 * WHY IT OPENS WITH SUGGESTIONS RATHER THAN A BLANK FORM
 * ------------------------------------------------------
 * Composing an order from nothing means somebody remembering what is running
 * low, which is how a kitchen finds out it has no onions at seven in the
 * evening. The system already knows what is below its reorder level and what
 * was last paid — so this starts as something to review.
 *
 * WHY NOTHING IS TICKED TO BEGIN WITH
 * -----------------------------------
 * A suggestion is a fact about the records; whether to buy against it is a
 * judgement about the week ahead, the supplier's delivery day and how much
 * cold room there is. A form that arrives pre-ticked gets sent unread, and
 * the order that gets sent unread is the one nobody meant.
 *
 * WHY ANYTHING CAN BE ADDED, NOT ONLY WHAT IS LOW
 * ------------------------------------------------
 * Because suggestions were the *only* thing this offered, and a shop that is
 * well stocked could not raise an order at all: the panel said "nothing is
 * below its reorder level" and stopped there, with no way to put a single item
 * on a purchase order.
 *
 * That is not how buying works. A delivery day is coming, or it is Thursday
 * and the weekend needs covering, or a supplier has a price on something. None
 * of that is a shortage, and all of it is an order somebody needs to place.
 *
 * WHAT IT REFUSES TO GUESS
 * ------------------------
 * Anything whose stock reads below nothing. That is not a shortage, it is a
 * record that has stopped describing the store, and ordering the difference
 * buys against a number known to be wrong — the more wrong, the bigger the
 * order.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

export function RaiseOrderPanel({ onClose, onRaised }) {
  const [supplierId, setSupplierId] = useState('');
  const [expected, setExpected] = useState('');
  const [picked, setPicked] = useState({});

  const suppliers = useQuery({
    queryKey: ['purchasing', 'suppliers'],
    queryFn: () => api.get('/purchasing/suppliers'),
    staleTime: 5 * 60_000,
  });

  const suggestions = useQuery({
    queryKey: ['purchasing', 'suggestions', supplierId],
    queryFn: () =>
      api.get(withQuery('/purchasing/reorder-suggestions', { supplier_id: supplierId })),
  });

  const rows = suggestions.data?.suggestions ?? [];
  const coverage = suggestions.data?.coverage;

  /**
   * Everything the shop buys, for the times nothing is running low.
   *
   * Fetched alongside the suggestions rather than behind a button, because a
   * buyer who has opened this panel is going to want it — and a list that
   * appears only after a second click is a list nobody finds.
   */
  const ingredients = useQuery({
    queryKey: ['purchasing', 'ingredients'],
    queryFn: () => api.get('/menu/ingredients?limit=500'),
    staleTime: 5 * 60_000,
  });

  const [adding, setAdding] = useState('');

  const suggested = new Set(rows.map((r) => r.ingredientId));

  /** What is on the order because somebody asked for it, not because it is low. */
  const extras = Object.keys(picked)
    .filter((id) => !suggested.has(id))
    .map((id) => ({
      ingredientId: id,
      ...(ingredients.data?.ingredients ?? []).find((i) => i.id === id),
    }))
    .filter((row) => row.name);

  function addOne(id) {
    const found = (ingredients.data?.ingredients ?? []).find((i) => i.id === id);
    if (!found || picked[id]) return;

    setPicked((was) => ({
      ...was,
      [id]: {
        quantity: '',
        // What it last cost, as a starting point somebody can correct — the
        // same courtesy the suggestions get.
        price: found.cost_per_unit != null ? String(found.cost_per_unit) : '',
        unitId: found.unit_id,
      },
    }));
  }

  const toggle = (row) =>
    setPicked((was) => {
      if (was[row.ingredientId]) {
        const { [row.ingredientId]: _removed, ...rest } = was;
        return rest;
      }
      return {
        ...was,
        [row.ingredientId]: {
          quantity: String(row.suggestedQuantity ?? ''),
          price: row.lastPrice != null ? String(row.lastPrice) : '',
          unitId: row.unitId,
        },
      };
    });

  const setField = (id, field, value) =>
    setPicked((was) => ({ ...was, [id]: { ...was[id], [field]: value } }));

  const chosen = Object.entries(picked);
  const total = chosen.reduce(
    (sum, [, line]) => sum + (Number(line.quantity) || 0) * (Number(line.price) || 0),
    0
  );

  // Every line needs a quantity and a price. A line with neither is not an
  // order for anything, and one with no price cannot be checked against the
  // invoice when it arrives.
  const incomplete = chosen.filter(
    ([, line]) => !(Number(line.quantity) > 0) || !(Number(line.price) > 0)
  );

  const raise = useMutation({
    mutationFn: () =>
      api.post('/purchasing/orders', {
        supplierId,
        // No branch sent: the server takes it from whoever is signed in,
        // rather than the screen working out an id it has no business knowing.
        expectedDelivery: expected || undefined,
        items: chosen.map(([ingredientId, line]) => ({
          ingredientId,
          unitId: line.unitId,
          quantity: Number(line.quantity),
          unitPrice: Number(line.price),
        })),
      }),
    onSuccess: onRaised,
  });

  const field =
    'h-10 rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink focus:border-brand-400 focus:outline-none';

  return (
    <div className="rounded-xl border border-rule bg-surface p-5">
      <h2 className="mb-3 font-medium text-ink">Raise an order</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Supplier</span>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            aria-label="Supplier"
            className={`${field} w-full`}
          >
            <option value="">Choose a supplier</option>
            {(suppliers.data?.suppliers ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Expected</span>
          <input
            type="date"
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            aria-label="Expected delivery"
            className={`${field} w-full`}
          />
        </label>
      </div>

      {coverage?.warning && (
        <p className="mt-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
          {coverage.warning}
        </p>
      )}

      <div className="mt-4">
        <h3 className="mb-2 text-sm font-medium text-ink-2">
          Below their level <span className="text-ink-3">({rows.length})</span>
        </h3>

        {suggestions.isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner label="Working out what is low" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-sm text-ink-3">Nothing is below its reorder level.</p>
        ) : (
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {rows.map((row) => {
              const line = picked[row.ingredientId];

              return (
                <div
                  key={row.ingredientId}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-rule px-3 py-2"
                >
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(line)}
                      onChange={() => toggle(row)}
                      aria-label={`Order ${row.name}`}
                      className="h-5 w-5 shrink-0 rounded border-rule accent-brand-400"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">{row.name}</span>
                      <span className="block text-xs text-ink-3">
                        {row.countIsImpossible ? (
                          // Said on the line, not only in the banner above —
                          // this is the row somebody is about to tick.
                          <span className="text-warn">
                            shows {row.inStock} in stock, which cannot be right — count it
                          </span>
                        ) : (
                          <>
                            {row.inStock} {row.unit} in stock, want {row.reorderLevel}
                            {row.lastPrice != null
                              ? ` · last paid ${formatPKR(row.lastPrice)} on ${row.lastBought}`
                              : ' · never bought before'}
                          </>
                        )}
                      </span>
                    </span>
                  </label>

                  {line && (
                    <>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={line.quantity}
                        onChange={(e) => setField(row.ingredientId, 'quantity', e.target.value)}
                        placeholder="Qty"
                        aria-label={`Quantity of ${row.name}`}
                        className={`${field} w-24 text-right tabular-nums`}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.price}
                        onChange={(e) => setField(row.ingredientId, 'price', e.target.value)}
                        placeholder="Price"
                        aria-label={`Unit price of ${row.name}`}
                        className={`${field} w-24 text-right tabular-nums`}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/**
        * Anything else the buyer wants on this order.
        *
        * Below the suggestions rather than above them, because what is running
        * low is still the first thing worth reading — this is the answer to
        * "and while we are ordering, add…".
        */}
      <div className="mt-5 border-t border-rule pt-4">
        <h3 className="mb-2 text-sm font-medium text-ink-2">Add anything else</h3>

        <select
          value={adding}
          onChange={(e) => {
            addOne(e.target.value);
            // Straight back to the prompt, so a second item is one action.
            setAdding('');
          }}
          aria-label="Add an ingredient to this order"
          className={`${field} w-full`}
          disabled={ingredients.isLoading}
        >
          <option value="">
            {ingredients.isLoading ? 'Loading what you buy…' : 'Pick an ingredient…'}
          </option>
          {(ingredients.data?.ingredients ?? [])
            .filter((i) => !picked[i.id] && !suggested.has(i.id))
            .map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
        </select>

        {extras.length > 0 && (
          <div className="mt-2 space-y-1">
            {extras.map((row) => {
              const line = picked[row.ingredientId];

              return (
                <div
                  key={row.ingredientId}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-rule px-3 py-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{row.name}</span>
                    <span className="block text-xs text-ink-3">
                      not low — added to this order
                    </span>
                  </span>

                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={line.quantity}
                    onChange={(e) => setField(row.ingredientId, 'quantity', e.target.value)}
                    placeholder="Qty"
                    aria-label={`Quantity of ${row.name}`}
                    className={`${field} w-24 text-right tabular-nums`}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.price}
                    onChange={(e) => setField(row.ingredientId, 'price', e.target.value)}
                    placeholder="Price"
                    aria-label={`Unit price of ${row.name}`}
                    className={`${field} w-24 text-right tabular-nums`}
                  />

                  <button
                    onClick={() =>
                      setPicked((was) => {
                        const { [row.ingredientId]: _gone, ...rest } = was;
                        return rest;
                      })
                    }
                    aria-label={`Take ${row.name} off this order`}
                    className="rounded-lg px-2 py-1 text-sm text-ink-3 hover:bg-surface-3"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {incomplete.length > 0 && (
        <p className="mt-3 text-sm text-warn">
          {incomplete.length} line{incomplete.length === 1 ? '' : 's'} still{' '}
          {incomplete.length === 1 ? 'needs' : 'need'} a quantity and a price. Without a price the
          invoice cannot be checked against the order.
        </p>
      )}

      {raise.isError && (
        <p className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {raise.error?.message ?? 'That did not work.'}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <span className="mr-auto text-sm text-ink-2">
          {chosen.length} line{chosen.length === 1 ? '' : 's'} · {formatPKR(total)}
        </span>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => raise.mutate()}
          disabled={!supplierId || chosen.length === 0 || incomplete.length > 0 || raise.isPending}
        >
          {raise.isPending ? 'Raising' : 'Raise it'}
        </Button>
      </div>
    </div>
  );
}
